import { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '@/setup/redux/RootReducer';
import {
  fetchTasks,
  fetchTasksForOrder,
  fetchTask,
  createTaskAsync,
  updateTaskAsync,
  deleteTaskAsync,
  assignTaskAsync,
  changeTaskStatusAsync,
  fetchMyTasks,
  fetchOverdueTasks,
  fetchTaskStatistics,
  fetchTaskStatuses,
  createTaskStatusAsync,
  fetchTaskPriorities,
  createTaskPriorityAsync,
  fetchUsers,
  fetchGroups,
  createGroupAsync,
  setFilters,
  setSelectedTasks,
  toggleTaskSelection,
  clearSelectedTasks,
  setCurrentTask,
  clearTasks,
  clearError,
} from '../redux/TaskRedux';
import {
  Task,
  TaskStatus,
  TaskPriority,
  User,
  CreateTaskRequest,
  UpdateTaskRequest,
  AssignTaskRequest,
  ChangeStatusRequest,
  CreateGroupRequest,
  GroupFilters,
  TaskFilters,
} from '../models/TaskModel';

// ==================== MAIN TASK HOOK ====================

export const useTasks = () => {
  const dispatch = useDispatch();
  const {
    tasks,
    allTasks,
    myTasks,
    myTasksCount,
    myTasksStatusCounts,
    myTasksPagination,
    overdueTasks,
    currentTask,
    loading,
    error,
    taskStatuses,
    taskPriorities,
    users,
    groups,
    statistics,
    selectedTasks,
    filters,
  } = useSelector((state: RootState) => state.tasks);

  // Fetch tasks with filters
  const getTasks = useCallback(
    (filters?: TaskFilters) => {
      dispatch(fetchTasks(filters));
    },
    [dispatch]
  );

  // Fetch tasks for specific order
  const getTasksForOrder = useCallback(
    (orderId: string, filters?: TaskFilters) => {
      dispatch(fetchTasksForOrder({ orderId, filters }));
    },
    [dispatch]
  );

  // Fetch single task
  const getTask = useCallback(
    (taskId: string) => {
      dispatch(fetchTask(taskId));
    },
    [dispatch]
  );

  // Create task
  const createTask = useCallback(
    (taskData: CreateTaskRequest) => {
      return dispatch(createTaskAsync(taskData));
    },
    [dispatch]
  );

  // Update task
  const updateTask = useCallback(
    (taskId: string, taskData: UpdateTaskRequest) => {
      return dispatch(updateTaskAsync({ id: taskId, taskData }));
    },
    [dispatch]
  );

  // Delete task
  const deleteTask = useCallback(
    (taskId: string) => {
      return dispatch(deleteTaskAsync(taskId));
    },
    [dispatch]
  );

  // Assign task
  const assignTask = useCallback(
    (taskId: string, assignData: AssignTaskRequest) => {
      return dispatch(assignTaskAsync({ taskId, assignData }));
    },
    [dispatch]
  );

  // Change task status
  const changeTaskStatus = useCallback(
    (taskId: string, statusData: ChangeStatusRequest) => {
      return dispatch(changeTaskStatusAsync({ taskId, statusData }));
    },
    [dispatch]
  );

  // Fetch my tasks
  const getMyTasks = useCallback(
    (filters?: TaskFilters) => {
      dispatch(fetchMyTasks(filters));
    },
    [dispatch]
  );

  // Fetch overdue tasks
  const getOverdueTasks = useCallback(
    (filters?: TaskFilters) => {
      dispatch(fetchOverdueTasks(filters));
    },
    [dispatch]
  );

  // Fetch statistics
  const getStatistics = useCallback(() => {
    dispatch(fetchTaskStatistics());
  }, [dispatch]);

  // Set filters
  const setTaskFilters = useCallback(
    (filters: TaskFilters) => {
      dispatch(setFilters(filters));
    },
    [dispatch]
  );

  // Selection management
  const selectTasks = useCallback(
    (taskIds: string[]) => {
      dispatch(setSelectedTasks(taskIds));
    },
    [dispatch]
  );

  const toggleSelection = useCallback(
    (taskId: string) => {
      dispatch(toggleTaskSelection(taskId));
    },
    [dispatch]
  );

  const clearSelection = useCallback(() => {
    dispatch(clearSelectedTasks());
  }, [dispatch]);

  // Set current task
  const selectCurrentTask = useCallback(
    (task: Task | null) => {
      dispatch(setCurrentTask(task));
    },
    [dispatch]
  );

  // Clear tasks and error
  const clearTasksData = useCallback(() => {
    dispatch(clearTasks());
  }, [dispatch]);

  const clearTasksError = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);

  return {
    // State with default values
    tasks: tasks || [],
    allTasks: allTasks || [],
    myTasks: myTasks || [],
    myTasksCount: myTasksCount ?? 0,
    myTasksStatusCounts: myTasksStatusCounts || {},
    myTasksPagination,
    overdueTasks: overdueTasks || [],
    currentTask,
    loading,
    error,
    taskStatuses: taskStatuses || [],
    taskPriorities: taskPriorities || [],
    users: users || [],
    groups: groups || [],
    statistics,
    selectedTasks: selectedTasks || [],
    filters: filters || {},

    // Actions
    getTasks,
    getTasksForOrder,
    getTask,
    createTask,
    updateTask,
    deleteTask,
    assignTask,
    changeTaskStatus,
    getMyTasks,
    getOverdueTasks,
    getStatistics,
    setTaskFilters,
    selectTasks,
    toggleSelection,
    clearSelection,
    selectCurrentTask,
    clearTasksData,
    clearTasksError,
  };
};

// ==================== TASK STATUSES HOOK ====================

export const useTaskStatuses = () => {
  const dispatch = useDispatch();
  const { taskStatuses, loading, error } = useSelector((state: RootState) => state.tasks);

  const getTaskStatuses = useCallback(() => {
    dispatch(fetchTaskStatuses());
  }, [dispatch]);

  const createTaskStatus = useCallback(
    (statusData: Partial<TaskStatus>) => {
      return dispatch(createTaskStatusAsync(statusData));
    },
    [dispatch]
  );

  return {
    taskStatuses: taskStatuses || [],
    loading,
    error,
    getTaskStatuses,
    createTaskStatus,
  };
};

// ==================== TASK PRIORITIES HOOK ====================

export const useTaskPriorities = () => {
  const dispatch = useDispatch();
  const { taskPriorities, loading, error } = useSelector((state: RootState) => state.tasks);

  const getTaskPriorities = useCallback(() => {
    dispatch(fetchTaskPriorities());
  }, [dispatch]);

  const createTaskPriority = useCallback(
    (priorityData: Partial<TaskPriority>) => {
      return dispatch(createTaskPriorityAsync(priorityData));
    },
    [dispatch]
  );

  return {
    taskPriorities: taskPriorities || [],
    loading,
    error,
    getTaskPriorities,
    createTaskPriority,
  };
};

// ==================== USERS HOOK ====================

export const useUsers = () => {
  const dispatch = useDispatch();
  const { users, loading, error } = useSelector((state: RootState) => state.tasks);

  const getUsers = useCallback(() => {
    dispatch(fetchUsers());
  }, [dispatch]);

  return {
    users: users || [],
    loading,
    error,
    getUsers,
  };
};

// ==================== GROUPS HOOK ====================

export const useGroups = () => {
  const dispatch = useDispatch();
  const { groups, loading, error } = useSelector((state: RootState) => state.tasks);

  const getGroups = useCallback((filters?: GroupFilters) => {
    dispatch(fetchGroups(filters));
  }, [dispatch]);

  const createGroup = useCallback(
    (groupData: CreateGroupRequest) => {
      return dispatch(createGroupAsync(groupData));
    },
    [dispatch]
  );

  return {
    groups: groups || [],
    loading,
    error,
    getGroups,
    createGroup,
  };
};

// ==================== TASK MANAGEMENT HOOK (Combined) ====================

export const useTaskManagement = (orderId?: string, companyId?: string) => {
  const {
    tasks,
    loading,
    error,
    taskStatuses,
    taskPriorities,
    users,
    groups,
    getTasksForOrder,
    createTask,
    assignTask,
    changeTaskStatus,
  } = useTasks();

  const { getTaskStatuses } = useTaskStatuses();
  const { getTaskPriorities } = useTaskPriorities();
  const { getGroups, createGroup } = useGroups();

  // Initialize data for order
  const initializeOrderTasks = useCallback((filters?: TaskFilters) => {
    if (orderId) {
      getTasksForOrder(orderId, filters);
      if (!filters) {
        getTaskStatuses();
        getTaskPriorities();
        getGroups(companyId ? { company: companyId } : undefined);
      }
    }
  }, [orderId, companyId, getTasksForOrder, getTaskStatuses, getTaskPriorities, getGroups]);

  return {
    // State with default values
    tasks: tasks || [],
    loading,
    error,
    taskStatuses: taskStatuses || [],
    taskPriorities: taskPriorities || [],
    users: users || [],
    groups: groups || [],

    // Actions
    initializeOrderTasks,
    createTask,
    assignTask,
    changeTaskStatus,
    createGroup,

    // Helpers
    getUserDisplayName: (user: User) => {
      return user.get_display_name || 
             user.full_name || 
             user.name || 
             user.username || 
             (user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.first_name || user.last_name) ||
             user.email ||
             'Unknown User';
    },
  };
};
